import React from "react";
import { useDroppable } from "@dnd-kit/core";

const assignRef = (ref, value) => {
    if (!ref) return;
    if (typeof ref === "function") {
        ref(value);
        return;
    }
    if (typeof ref === "object") {
        ref.current = value;
    }
};

function Droppable({
    id = "droppable",
    children,
    style: overrideStyle = {},
    data,
    showPlaceholder = true,
    placeholderStyle = {},
    layout = "grid", // "vertical" | "horizontal" | "grid"
    gap = 12,
    gridColumns = 3,
    isDisabled = false,
}) {
    const { isOver, setNodeRef } = useDroppable({ id, data, disabled: isDisabled });
    const child = React.Children.only(children);
    const childRef = React.useRef(null);

    const mergedChildRef = (node) => {
        assignRef(childRef, node);
        assignRef(child.ref, node);
    };

    const layoutStyle = React.useMemo(() => {
        if (layout === "horizontal") {
            return { display: "flex", flexDirection: "row", gap, alignItems: "stretch" };
        }
        if (layout === "grid") {
            return {
                display: "grid",
                gridTemplateColumns: `repeat(${Math.max(3, gridColumns)}, minmax(0, 1fr))`,
                gap,
                width: "100%",
                height: "100%",
                minHeight: "100%",
                maxHeight: "100%",
                minWidth: 0,
                boxSizing: "border-box",
                overflowX: "hidden",
                overflowY: "auto",
                alignContent: "start",
                justifyItems: "stretch",
            };
        }
        return { display: "flex", flexDirection: "column", gap, alignItems: "stretch" };
    }, [layout, gap, gridColumns]);

    const style = {
        width: "100%",
        maxHeight: "100%",
        minWidth: 0,
        boxSizing: "border-box",
        overflowX: "hidden",
        overflowY: "auto",
        ...layoutStyle,
        ...child.props?.style,
        ...overrideStyle,
        color: isOver && !isDisabled ? "green" : child.props?.style?.color,
    };

    const placeholder = showPlaceholder && !isDisabled ? (
        <div
            className="droppable-placeholder"
            aria-hidden
            style={{
                display: isOver ? "block" : "none",
                marginTop: gap / 2,
                marginBottom: gap / 2,
                ...placeholderStyle,
            }}
        />
    ) : null;

    return (
        <div
            ref={isDisabled ? null : setNodeRef}
            style={{
                position: "relative",
                width: "100%",
                height: "100%",
                minHeight: "100%",
                maxHeight: "100%",
                minWidth: 0,
                boxSizing: "border-box",
                overflowX: "hidden",
                overflowY: "hidden",
            }}
        >
            {React.cloneElement(child, {
                ref: mergedChildRef,
                style,
            })}
            {placeholder}
        </div>
    );
}

export default Droppable;
